// Extension to render things under a block
import { sortBy } from "lodash";
import { EditorState, Plugin } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";
import invariant from "tiny-invariant";

import { Extension } from "../extension";
import { isBlock } from "../utils";
import { getBlockBoundaryState } from "./blockBoundary";
import { isInCollapsedSection } from "./collapse";
import { BLOCK_ANNOTATION_ORDER } from "./prompt";

import collapsedStyles from "../css/collapse.module.css";

export const ACTION_ITEM_PROGRESS_ORDER = 0;
export const ACTION_ITEM_INTEGRATION_LINK_ANNOTATION_ORDER = 1;
export const ASANA_ANNOTATION_ORDER = 2;
export const COMMENT_ANNOTATION_ORDER = 3;
export const REACTION_ANNOTATION_ORDER = 4;

export function addAnnotation(view: EditorView, blockKey: string, element: Element, order = 0) {
	const ext = view.extensions.get("BlockAnnotation") as BlockAnnotation;
	if (!ext) {
		console.warn("BlockAnnotation is not enabled");
	}
	ext.addAnnotation(blockKey, element, order);
}

export function removeAnnotation(view: EditorView, blockKey: string, element: Element) {
	const ext = view.extensions.get("BlockAnnotation") as BlockAnnotation;
	if (!ext) {
		console.warn("BlockAnnotation is not enabled");
	}
	ext.removeAnnotation(blockKey, element);
}

export function flushAnnotations(view: EditorView) {
	const ext = view.extensions.get("BlockAnnotation") as BlockAnnotation;
	if (!ext) {
		console.warn("BlockAnnotation is not enabled");
	}
	ext.flush(true);
}

type AnnotationQueueItem = (
	| {
			type: "ADD";
			order: number;
	  }
	| {
			type: "REMOVE";
	  }
) & {
	element: Element;
	blockKey: string;
};

class BlockAnnotation extends Extension {
	name = "BlockAnnotation";
	decorations = new Map<string, Decoration>();
	annotations = new Map<string, [Element, number][]>();
	containers = new Map<string, HTMLDivElement>();
	queue: AnnotationQueueItem[] = [];

	addAnnotation = (blockKey: string, element: Element, order = 0) => {
		this.queue.push({ type: "ADD", order: order || 0, blockKey, element });
	};

	removeAnnotation = (blockKey: string, element: Element) => {
		this.queue.push({ type: "REMOVE", blockKey, element });
	};

	flush = (dispatch = false) => {
		const keysWithEmptyAnnotations = new Set<string>();
		while (this.queue.length) {
			const item = this.queue.shift();
			if (!item) continue;
			const current = this.annotations.get(item.blockKey);
			if (item.type === "REMOVE") {
				if (!current) continue;
				const index = current.findIndex(([element]) => element === item.element);
				if (index > -1) {
					current.splice(index, 1);
				}
				if (!current.length) {
					keysWithEmptyAnnotations.add(item.blockKey);
				}
			} else if (item?.type === "ADD") {
				if (current) {
					const index = current.findIndex(([element]) => element === item.element);
					if (index > -1) {
						current.splice(index, 1, [item.element, item.order]);
					} else {
						current.push([item.element, item.order]);
					}
				} else {
					this.annotations.set(item.blockKey, [[item.element, item.order]]);
				}
				keysWithEmptyAnnotations.delete(item.blockKey);
			}
		}
		keysWithEmptyAnnotations.forEach(k => this.annotations.delete(k));
		if (dispatch && this.editorView) {
			this.editorView.dispatch(this.editorView.state.tr);
		}
	};

	decorationToDOM = (blockKey: string) => (view: EditorView, getPos: () => number | undefined) => {
		let annotationsContainer = this.containers.get(blockKey);
		if (!annotationsContainer) {
			annotationsContainer = document.createElement("div");
			annotationsContainer.setAttribute("pmannotations", "");
			this.containers.set(blockKey, annotationsContainer);
		}

		const getPosRes = getPos();
		// we don't find any cases that 'getPos' will return an 'undefined'
		if (!getPosRes) return document.createElement("div");

		const $widgetPos = view.state.doc.resolve(getPosRes);
		const node = $widgetPos.nodeBefore;
		invariant(node && isBlock(node));
		const isTalkingItem = ["agenda", "todo", "goal"].includes(node.type["name"]);
		let indentationLevel = Number.parseInt(node.attrs["indent"] || "0");
		if (isTalkingItem) {
			indentationLevel += 1;
		}

		if (indentationLevel) {
			annotationsContainer.setAttribute("prosemirror-indent", String(indentationLevel));
		} else {
			annotationsContainer.removeAttribute("prosemirror-indent");
		}

		const isHidden = isInCollapsedSection(view.state, node);
		if (isHidden) {
			annotationsContainer.classList.add(collapsedStyles.hidden);
		} else {
			annotationsContainer.classList.remove(collapsedStyles.hidden);
		}

		const children = this.annotations.get(blockKey);
		if (children) {
			const sorted = sortBy(children, i => i[1]).map(i => i[0]);
			const existingChildren = annotationsContainer.children;
			let changed = sorted.length !== existingChildren.length;
			if (!changed) {
				for (const [i, child] of sorted.entries()) {
					if (child !== existingChildren[i]) {
						changed = true;
						break;
					}
				}
			}
			if (changed) {
				// .replaceChildren fails on some typescript version
				while (annotationsContainer.firstChild) {
					annotationsContainer.removeChild(annotationsContainer.firstChild);
				}
				annotationsContainer.append(...sorted);
			}
		}
		return annotationsContainer;
	};

	updateDecorations = (state: EditorState) => {
		const blockBoundaryState = getBlockBoundaryState(state);
		if (!blockBoundaryState) return;
		const { blockBoundaries } = blockBoundaryState;
		this.decorations.clear();
		this.annotations.forEach((annos, blockKey) => {
			if (!this.decorations.has(blockKey)) {
				const boundary = blockBoundaries[blockKey];
				if (boundary) {
					this.decorations.set(
						blockKey,
						Decoration.widget(boundary[1], this.decorationToDOM(blockKey), { side: BLOCK_ANNOTATION_ORDER }),
					);
				}
			}
		});
	};

	get plugins() {
		const ext = this;
		const blockAnnotationPlugin = new Plugin({
			state: {
				init: (_config, _state) => {
					return {};
				},
				apply: (_tr, value, _oldState, newState) => {
					ext.flush();
					ext.updateDecorations(newState);
					return value;
				},
			},
			props: {
				decorations: state => {
					const decos: Decoration[] = [];
					ext.decorations.forEach(v => {
						decos.push(v);
					});
					return DecorationSet.create(state.doc, decos);
				},
			},
		});
		return [blockAnnotationPlugin];
	}
}

export default BlockAnnotation;
